import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { scaleImageToFixedSize } from "./ImageResize";
import { updateUser } from "../../redux/actions/userActions";
import { setUserProfile } from "../../redux/slices/authSlice";
import { UploadImageFilledIcon } from "./Icons";
import FullScreenOverlay from "./FullScreenOverlay";
import defaultProfileImage from "../../assets/images/profile/default.webp"; // Default profile image

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB
const FIXED_SIZE = 140;

const EditableAvatar = ({ user }) => {
  const dispatch = useDispatch();
  const [avatarPreview, setAvatarPreview] = useState(
    user.profile_picture || ""
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Open the hidden file input when the upload icon is clicked
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check file size; if too big, set error message and return.
      if (file.size > MAX_FILE_SIZE) {
        setError("File size exceeds 4MB. Please select a smaller file.");
        return;
      }

      setError("");
      setSelectedFile(file);
      // Update preview immediately so the user can see the new image
      setAvatarPreview(URL.createObjectURL(file));
      // Ask for confirmation to update the avatar
      setShowConfirm(true);
    }
  };

  // Confirm avatar update: scale the image and update the server.
  const handleConfirm = async () => {
    setShowConfirm(false);
    try {
      // Scale the image to fixed dimensions.
      const scaledFile = await scaleImageToFixedSize(
        selectedFile,
        FIXED_SIZE,
        FIXED_SIZE
      );
      // Prepare payload. Here we assume updateUser can update the avatar with a partial update.
      const formData = { avatarFile: scaledFile };
      const updatedResponse = await dispatch(
        updateUser({ userId: user.id, formData })
      );
      if (updatedResponse && updatedResponse.payload) {
        // Update the user profile in the Redux store.
        const newProfilePicture = updatedResponse.payload.profile_picture;
        setAvatarPreview(newProfilePicture);
        dispatch(setUserProfile(updatedResponse.payload));
      }
      // Optionally, you could display a success message here.
    } catch (err) {
      console.error("Error updating avatar:", err);
      // If the update fails, revert preview to the existing avatar.
      setAvatarPreview(user.profile_picture);
    } finally {
      // Reset the selected file so that the same file can be selected again.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFile(null);
    }
  };

  // If user cancels, revert to the previous avatar
  const handleCancel = () => {
    setShowConfirm(false);
    setAvatarPreview(user.profile_picture);
    setSelectedFile(null);

    // Reset file input so that the same file can be re-selected.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <AvatarContainer>
        <Avatar style={{ width: FIXED_SIZE, height: FIXED_SIZE }}>
          <img
            src={avatarPreview || defaultProfileImage}
            alt="Avatar"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        </Avatar>
        <AddAvatarButton type="button" onClick={openFilePicker}>
          <UploadImageFilledIcon />
        </AddAvatarButton>
        <HiddenFileInput
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </AvatarContainer>

      {showConfirm && (
        <FullScreenOverlay
          width="400px"
          height="200px"
          isPopup={true}
          popupType="confirm"
          buttons={[
            {
              label: "Cancel",
              onClick: handleCancel,
              variant: "secondary",
            },
            { label: "Confirm", onClick: handleConfirm, variant: "primary" },
          ]}
        >
          <h5 className="semi-bold">Update Avatar</h5>
          <p>Are you sure you want to update your avatar?</p>
        </FullScreenOverlay>
      )}
    </>
  );
};

export default EditableAvatar;

/*---------------- Styled Components ----------------*/
const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Avatar = styled.div`
  border-radius: 50%;
  overflow: hidden;
  background: #ccc;
`;

const AddAvatarButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;
